import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { revealUp } from '../lib/anim';
import { scrollToId } from './SmoothScroll';
import { FAQ } from '../data/portfolio';

function FaqItem({ item, i, open, onToggle }) {
  return (
    <motion.div
      variants={revealUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      custom={i}
      className="border-b border-border"
    >
      <button
        onClick={onToggle}
        data-testid={`faq-trigger-${i}`}
        aria-expanded={open}
        className="group flex w-full items-center justify-between gap-6 py-7 text-left"
      >
        <span className="font-display text-xl font-medium tracking-tight text-foreground transition-colors md:text-2xl">
          {item.q}
        </span>
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border transition-all duration-500 ${
            open ? 'rotate-45 bg-foreground text-background' : 'text-foreground'
          }`}
        >
          <Plus className="h-4 w-4" />
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p
          data-testid={`faq-answer-${i}`}
          className="max-w-3xl pb-8 pr-16 text-[15px] leading-relaxed text-muted-foreground"
        >
          {item.a}
        </p>
      </motion.div>
    </motion.div>
  );
}

export default function Faq() {
  const [open, setOpen] = useState(0);

  // Inject Schema.org FAQPage structured data (JSON-LD) for Google rich results.
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-jsonld';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('faq-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32"
    >
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="md:sticky md:top-32">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              (FAQ)
            </span>
            <h2 className="mt-6 font-display text-4xl font-extrabold leading-[0.9] tracking-tighter text-foreground md:text-6xl">
              Domande
              <br />
              frequenti.
            </h2>
            <p className="mt-8 max-w-xs text-base leading-relaxed text-muted-foreground">
              Tutto ciò che vuoi sapere prima di iniziare. Hai un altro dubbio?
            </p>
            <button
              onClick={() => scrollToId('contact')}
              data-testid="faq-contact-cta"
              className="mt-6 font-mono text-xs uppercase tracking-[0.15em] text-foreground underline underline-offset-4 transition-opacity hover:opacity-60"
            >
              Scrivimi direttamente
            </button>
          </div>
        </div>

        <div className="md:col-span-8">
          {FAQ.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              i={i}
              open={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
